import * as FileSaver from 'file-saver';
import {ECharts} from 'echarts';
import {PeriodFilter} from '@core/interfaces/engin/load-forecast/load-forecast';

export enum ECHARTS_TYPE {
    CUSTOM_BAR_STACKED = 1, // e.g., analyzer EOL metrics
    BAR_V, // Temporary: planner, project view
    CUSTOM_COL_STACKED, // e.g., chartData.data.name
    COL_STACKED_CLUSTERED, // e.g., optimizer, forecaster
    PIE, // e.g., analyzer EOL metrics
    DUAL_PIE, // e.g., ACA Data Gaps
    LINE, // e.g., Data audit tool
    CUSTOM_LINE, // e.g., connectivity analysis (Load Profile)
    CUSTOM_BAR, // e.g., connectivity analysis (Load Profile)
    BAR, // e.g., Data audit tool
    SCATTER, // e.g. Evaluation data audit tool, LIVE dashboard > Sf6 gas leak
    SCATTER_ASSET_MAINTENANCE, // e.g. Asset maintenance
    SCATTER_MULTI_SERIES_AND_CATEGORY,
    SINGLE_SERIES_BAR_LINE, // e.g. forecaster (station availability, line availability)
    MULTI_LINE, // e.g., Data audit tool
    TIMESERIES, // e.g., LIVE dense time-series (line or area)
    LOAD_FORECAST_LINE, // e.g. Load Forecast page
    LINE_WITH_X_AXIS_DATES, // xAxis Data is shown as rows, not columns e.g. Load Profile Analysis -> Linked meters page
    HORIZONTAL_BAR_CHART,
}
export enum SIMPLE_DATA_TYPE {
    DEFAULT = 'DEFAULT',
}
export interface ChartDownloadData {
    header: any[];
    data: any[];
}
export interface SimpleDataTabletDownloadData {
    header: any[];
    data: any;
}
export class ChartDataDownloadService {
    // Prepare chart data for download then send to browser
    static prepareAndDownloadChartData(options: any, chartType: ECHARTS_TYPE, filename: string) {
        const outputHeader = [];
        const outputObjectArray = [];
        let xAxisName, yAxisName;
        switch (chartType) {
            case ECHARTS_TYPE.CUSTOM_BAR_STACKED: {
                // Create header from series names
                outputHeader.push('Category');
                for (let i = 0; i < options.series.length; i++) {
                    outputHeader.push(options.series[i].name);
                }

                // Add data rows from each independent series/bar
                if (options.yAxis[0].data && options.yAxis[0].data != null) {
                    // Multi bar has yAxis data element
                    for (let yIndex = 0; yIndex < options.yAxis[0].data.length; yIndex++) {
                        const tempDataEntry = {};
                        tempDataEntry['Category'] = options.yAxis[0].data[yIndex];

                        // Look for custom data object "origValue" in cases when custom formatting was used
                        for (let i = 0; i < options.series.length; i++) {
                            tempDataEntry[`field_${i}`] = options.series[i].data[yIndex].origValue
                                ? options.series[i].data[yIndex].origValue
                                : options.series[i].data[yIndex].value;
                        }
                        outputObjectArray.push(tempDataEntry);
                    }
                } else {
                    // Single bar - no yAxis data element
                    const tempDataEntry = {};
                    tempDataEntry['Category'] = 'All';
                    for (let i = 0; i < options.series.length; i++) {
                        tempDataEntry[`field_${i}`] = options.series[i].data[0].origValue
                            ? options.series[i].data[0].origValue
                            : options.series[i].data[0].value;
                    }
                    outputObjectArray.push(tempDataEntry);
                }

                break;
            }
            case ECHARTS_TYPE.BAR_V: {
                outputHeader.push('Alternative');
                outputHeader.push('Total Cost of Ownership [$M]');
                for (let xIndex = 0; xIndex < options.xAxis[0].data.length; xIndex++) {
                    const tempDataEntry = {};
                    tempDataEntry['Alternative'] = options.xAxis[0].data[xIndex];
                    tempDataEntry['Total Cost of Ownership [$M]'] = options.series[0].data[xIndex].value;
                    outputObjectArray.push(tempDataEntry);
                }
                break;
            }
            case ECHARTS_TYPE.COL_STACKED_CLUSTERED: {
                // Create header row
                xAxisName = options.xAxis[0].name;
                outputHeader.push(xAxisName);
                for (let seriesIndex = 0; seriesIndex < options.series.length; seriesIndex++) {
                    outputHeader.push(options.series[seriesIndex].name);
                }

                /*
                 * Iterate over x-axis values and pull values for each series row-by-row.
                 * - Stacked series objects show with different names in the series list
                 * - Pull custom data format for series names vs. codes, and handle total lines
                 */
                for (let xIndex = 0; xIndex < options.xAxis[0].data.length; xIndex++) {
                    const tempDataEntry = {};
                    tempDataEntry[`field_${xAxisName}`] = !isNaN(Number(options.xAxis[0].data[xIndex]))
                        ? Number(options.xAxis[0].data[xIndex])
                        : options.xAxis[0].data[xIndex];

                    for (let seriesIndex = 0; seriesIndex < options.series.length; seriesIndex++) {
                        // Push data; for totals, read the second data element
                        if (
                            options.series[seriesIndex].name.includes('total') &&
                            Array.isArray(options.series[seriesIndex].data[xIndex])
                        ) {
                            tempDataEntry[`field_${seriesIndex}`] = options.series[seriesIndex].data[xIndex][1];
                        } else {
                            tempDataEntry[`field_${seriesIndex}`] = options.series[seriesIndex].data[xIndex];
                        }
                    }
                    outputObjectArray.push(tempDataEntry);
                }
                break;
            }
            case ECHARTS_TYPE.CUSTOM_COL_STACKED: {
                const series = options.series;
                // Create header row
                const xAxisName = options.xAxis[0].name;

                outputHeader.push(xAxisName);

                // Iterate over x-axis values and pull values for each series row-by-row
                for (let xIndex = 0; xIndex < options.xAxis[0].data.length; xIndex++) {
                    const tempDataEntry = {};

                    tempDataEntry[`field_${xAxisName}`] = options.xAxis[0].data[xIndex];

                    series.forEach((seriesData, seriesIndex) => {
                        const columnHeader = seriesData.name;

                        if (!outputHeader.includes(columnHeader)) {
                            outputHeader.push(columnHeader);
                        }

                        if (columnHeader.toLowerCase().includes('total')) {
                            tempDataEntry[`field_${seriesIndex}`] = seriesData.data[xIndex]
                                ? seriesData.data[xIndex]
                                : 0;
                        } else {
                            tempDataEntry[columnHeader] = seriesData.data[xIndex]?.origValue;
                        }
                    });

                    outputObjectArray.push(tempDataEntry);
                }
                break;
            }
            case ECHARTS_TYPE.PIE: {
                outputHeader.push('Category');
                outputHeader.push('Value');

                for (let i = 0; i < options.series[0].data.length; i++) {
                    const tempDataEntry = {};
                    tempDataEntry['Category'] = options.series[0].data[i].name;
                    tempDataEntry['Cost'] = options.series[0].data[i].value;
                    outputObjectArray.push(tempDataEntry);
                }
                break;
            }
            case ECHARTS_TYPE.DUAL_PIE: {
                outputHeader.push('Category');
                outputHeader.push('Value');
                options.series.map((item) => {
                    item.data.map((data) => {
                        const tempDataEntry = {};
                        tempDataEntry['Category'] = data.name;
                        tempDataEntry['Cost'] = data.value;
                        outputObjectArray.push(tempDataEntry);
                    });
                });
                break;
            }
            case ECHARTS_TYPE.CUSTOM_LINE:
            case ECHARTS_TYPE.CUSTOM_BAR:
            case ECHARTS_TYPE.LINE: {
                const xAxisValue = options.xAxis.data;
                outputHeader.push('Category');
                const tempDataArray = [];
                xAxisValue.map((xAxis, index) => {
                    outputHeader.push(xAxis);
                    options.series.map((item) => {
                        if (item.id) {
                            const tempDataObj = {};
                            tempDataObj['Category'] = item.name;
                            tempDataObj[xAxis] = item.data[index].formattedValue || item.data[index];
                            tempDataArray.push(tempDataObj);
                        }
                    });
                });

                // merge object by same Category
                const result = {};
                tempDataArray.forEach((item) => (result[item.Category] = {...result[item.Category], ...item}));
                Object.values(result).map((res) => {
                    outputObjectArray.push(res);
                });
                break;
            }
            case ECHARTS_TYPE.LOAD_FORECAST_LINE: {
                const xAxisValue = options.xAxis.data;
                outputHeader.push('Category');
                const tempDataArray = [];
                xAxisValue.map((xAxis, index) => {
                    outputHeader.push(xAxis);
                    options.series.map((item) => {
                        const tempDataObj = {};
                        let tempDataObjValue;
                        if (item.data?.length > 0) {
                            tempDataObjValue = item.data[index]?.formattedValue
                                ? item.data[index]?.formattedValue
                                : item.data[index];
                        } else {
                            if (item.markLine) {
                                tempDataObjValue = Object.values(item.markLine.data[0])[0];
                            } else {
                                tempDataObjValue = '';
                            }
                        }

                        tempDataObj['Category'] = item.name;
                        tempDataObj[xAxis] = tempDataObjValue;
                        tempDataArray.push(tempDataObj);
                    });
                });

                // merge object by same Category
                const result = {};
                tempDataArray.forEach((item) => (result[item.Category] = {...result[item.Category], ...item}));
                Object.values(result).map((res) => {
                    outputObjectArray.push(res);
                });
                break;
            }
            case ECHARTS_TYPE.LINE_WITH_X_AXIS_DATES: {
                // Get the xAxis values (these will be the row headers)
                const xAxisValue = options.xAxis?.data || options.xAxis[0]?.data || [];

                // Start with the header, 'Date' as the first column, followed by the series names
                outputHeader.push('Date');

                // Initialize an array to store each row of the output
                const tempDataEntry: {[key: string]: string}[] = [];

                // Loop through each xAxis value (representing the rows)
                xAxisValue.forEach((xAxis, index) => {
                    // Create a row starting with the xAxis value (the 'Date')
                    const row = {Date: xAxis};

                    // For each series, add the corresponding data point for the current xAxis value
                    options.series.forEach((item) => {
                        // Only process series associated with the first xAxis (xAxisIndex: 0)
                        if (!item.xAxisIndex) {
                            if (!outputHeader.includes(item.name)) {
                                outputHeader.push(item.name);
                            }

                            if (item.data?.length > 0 && item.data[index]?.formattedValue) {
                                row[item.name] = item.data[index]?.formattedValue;
                            }

                            if (item.markLine && item.markLine.data.length > 0) {
                                row[item.name] = item.markLine.data[0].yAxis;
                            }
                        }
                    });

                    // Push the row into the data array
                    tempDataEntry.push(row);
                });

                // Convert the rows to the final output array
                tempDataEntry.forEach((row) => {
                    outputObjectArray.push(row);
                });

                break;
            }

            case ECHARTS_TYPE.SCATTER: {
                const xAxisValue = options.series[0].data;
                outputHeader.push('Category');

                const tempDataArray = [];

                // Loop through xAxis values
                xAxisValue.forEach((xAxis, index) => {
                    outputHeader.push(xAxis[0]);

                    // Loop through series data to build the data array
                    options.series.forEach((item) => {
                        if (item.id) {
                            const tempDataObj = {};
                            tempDataObj['Category'] = item.name;
                            tempDataObj[xAxis[0]] = item.data[index][1]; // Assign corresponding y-axis value
                            tempDataArray.push(tempDataObj);
                        }
                    });
                });

                // Merge objects by the same Category
                const result = tempDataArray.reduce((acc, item) => {
                    acc[item.Category] = {...acc[item.Category], ...item}; // Merge data for the same category
                    return acc;
                }, {});

                // Push the final merged objects into the output array
                outputObjectArray.push(...Object.values(result));
                break;
            }

            case ECHARTS_TYPE.SCATTER_ASSET_MAINTENANCE: {
                const rows: string[] = options.xAxis[0].data; // X-axis data (quarters)
                const years: string[] = options.xAxis[1].data; // Year data from the second X-axis
                const categories: string[] = options.yAxis.data; // y-axis categories
                const xAxisName = 'Time Period'; // Label for the first column

                const seriesList: string = options.series.map((item) => item.id && item.name); // Get the names of all series

                // The first column will be 'Time Period', followed by the series names as column headers
                outputHeader.push(xAxisName, ...seriesList);

                const tempDataArray = [];

                options.series.forEach((seriesItem) => {
                    if (seriesItem.id && seriesItem.data.length > 0) {
                        seriesItem.data.forEach((item) => {
                            let category: string = categories[item[1]];
                            let year: string = years[item[0]];
                            let quarter: string = rows[item[0]];
                            if (category != '' && year != '' && quarter != '') {
                                const row = {};
                                row[xAxisName] = `${year} - Q${quarter}`;
                                row[seriesItem.name] = item[2] ? item[2] : 0;
                                outputObjectArray.push(row);
                            }
                        });
                    }
                });

                // Add the rows to the final output array for the CSV
                tempDataArray.forEach((item) => {
                    outputObjectArray.push(item);
                });

                break;
            }
            case ECHARTS_TYPE.SCATTER_MULTI_SERIES_AND_CATEGORY: {
                const rows = options.xAxis[0].data; // X-axis data (quarters)
                const years = options.xAxis[1].data; // Year data from the second X-axis
                const categories = options.yAxis.data; // y-axis categories
                const xAxisName = 'Time Period'; // Label for the first column

                outputHeader.push(xAxisName, options.seriesLabel, options.yAxis.name, options.valueLabel);

                options.series.forEach((seriesItem) => {
                    if (seriesItem.id && seriesItem.data.length > 0) {
                        seriesItem.data.forEach((item) => {
                            let category = categories[item[1]];
                            let year = years[item[0]];
                            let quarter = rows[item[0]];
                            if (category != '' && year != '' && quarter != '') {
                                const row = {};
                                row[xAxisName] = `${year} - Q${quarter}`;
                                row[options.seriesLabel] = seriesItem.name;
                                row[options.yAxis.name] = category;
                                row[options.valueLabel] = item[2] ? item[2] : 0;
                                outputObjectArray.push(row);
                            }
                        });
                    }
                });

                break;
            }
            case ECHARTS_TYPE.HORIZONTAL_BAR_CHART: {
                outputHeader.push(options.yAxis.name);
                if (options?.barCategory) {
                    outputHeader.push(options?.barCategory);
                }
                outputHeader.push(options.xAxis.name);
                for (let yIndex = 0; yIndex < options.yAxis.data.length; yIndex++) {
                    const tempDataEntry = {};
                    tempDataEntry[options.yAxis.name] = options.yAxis.data[yIndex].replace(
                        options.yAxis.labelFormatterRegex,
                        '',
                    );
                    if (options?.barCategory) {
                        tempDataEntry[options?.barCategory] = options.series[0].data[yIndex].category;
                    }
                    tempDataEntry[options.xAxis.name] = options.series[0].data[yIndex].value;
                    outputObjectArray.push(tempDataEntry);
                }
                break;
            }
            case ECHARTS_TYPE.BAR: {
                const yAxisValue = options.yAxis.data;
                outputHeader.push('Category');
                const tempDataArray = [];
                yAxisValue.map((yAxis, index) => {
                    outputHeader.push(yAxis);
                    options.series.map((item) => {
                        if (item.id) {
                            const tempDataObj = {};
                            tempDataObj['Category'] = item.name;
                            tempDataObj[yAxis] = item.data[index];
                            tempDataArray.push(tempDataObj);
                        }
                    });
                });

                // merge object by same Category
                const result = {};
                tempDataArray.forEach((item) => (result[item.Category] = {...result[item.Category], ...item}));
                Object.values(result).map((res) => {
                    outputObjectArray.push(res);
                });

                break;
            }
            case ECHARTS_TYPE.SINGLE_SERIES_BAR_LINE: {
                /*
                 * Chart type: bar
                 * Number of series: 1
                 * Extract header: (xAxis name (default: Category), series name (default: yAxis name))
                 * Extract body: one row per element of series.data
                 */
                // Create header row
                const xAxis = options?.xAxis[0] || options?.xAxis;
                const yAxis = options?.yAxis[0] || options?.yAxis;
                xAxisName = xAxis?.name || 'Category';
                yAxisName = yAxis?.name || 'Value';
                outputHeader.push(xAxisName);
                outputHeader.push(options.series[0]?.name || yAxisName);

                // Iterate over x-axis values and pull values from single series row-by-row
                for (let ind = 0; ind < xAxis.data.length; ind++) {
                    const tempDataEntry = {};
                    tempDataEntry[`field_${xAxisName}`] = !isNaN(Number(xAxis.data[ind]))
                        ? Number(xAxis.data[ind])
                        : xAxis.data[ind];
                    tempDataEntry[`field_${0}`] = options.series[0].data[ind];
                    outputObjectArray.push(tempDataEntry);
                }
                break;
            }
            case ECHARTS_TYPE.TIMESERIES: {
                /*
                 * Options:
                 * - Options.xAxis has no data
                 * - Options.series has one or more elements; each element has data array where each element is time/value pairs
                 * Output:
                 * - First column: header "date" or "time" from Options.xAxis.name
                 * - One column per series
                 * - One row per x-Axis value
                 */
                const multiSeries = options.series;
                const xAxisData = options.series[0]?.data?.map((e) => e[0]);
                const xAxisHeader = options.xAxis?.name ? options.xAxis?.name : 'Date/Time';
                // Create header
                outputHeader.push(xAxisHeader);
                multiSeries.forEach((series) => {
                    const seriesName = series.name;
                    outputHeader.push(seriesName);
                });
                // Populate rows one one at a time
                xAxisData.forEach((val, index) => {
                    const tempDataObj = {};
                    tempDataObj[xAxisHeader] = val;
                    multiSeries.forEach((series) => {
                        const seriesName = series.name;
                        tempDataObj[seriesName] = series.data[index][1]; // [date/time, value]
                    });
                    outputObjectArray.push(tempDataObj);
                });
                break;
            }
        }

        // Send data to be downloaded to browser
        const customData: ChartDownloadData = {
            header: outputHeader,
            data: outputObjectArray,
        };
        ChartDataDownloadService.downloadChartData(customData, filename);
    }

    static prepareAndDownloadSimpleDataTableData(
        options: any,
        chartType: SIMPLE_DATA_TYPE,
        filename: string,
        period: PeriodFilter,
    ) {
        let outputHeader = [];
        const outputObjectArray = [];
        switch (chartType) {
            case SIMPLE_DATA_TYPE.DEFAULT: {
                options.map((obj) => {
                    const row = this.objectToMap(obj, period);
                    outputObjectArray.push(row);
                });
                outputHeader = Array.from(outputObjectArray[0].keys());
                break;
            }
        }
        // Send data to be downloaded to browser
        const customData: SimpleDataTabletDownloadData = {
            header: outputHeader,
            data: outputObjectArray,
        };
        ChartDataDownloadService.downloadSimpleTableData(customData, filename);
    }
    //
    static objectToMap(obj: Record<string, any>, period: PeriodFilter): Map<string, any> {
        const entries = Object.entries(obj);
        const alphaEntries = entries.filter(([key]) => /^[a-zA-Z]/.test(key));
        const nonAlphaEntries = entries.filter(([key]) => {
            const baseKey = key.includes('-child') ? key.split('-child')[0] : key;
            return (
                !/^[a-zA-Z]/.test(key) && Number(baseKey) >= Number(period.min) && Number(baseKey) <= Number(period.max)
            );
        });

        const orderedMap = new Map<string, any>();

        [...alphaEntries, ...nonAlphaEntries].forEach(([key, value]) => {
            const baseKey = key.includes('-child') ? key.split('-child')[0] : key;
            if (orderedMap.has(baseKey)) {
                const existing = orderedMap.get(baseKey);
                orderedMap.set(baseKey, `${existing}/${value}`);
            } else {
                orderedMap.set(baseKey, value);
            }
        });
        return orderedMap;
    }

    // Prepare CSV data and send to browser
    static downloadSimpleTableData(customData: SimpleDataTabletDownloadData, fileName: string) {
        const headerRow: string[] = customData.header;
        const arrData: any = customData.data;
        const replacer = (key, value) => (value === null ? '' : value);

        const csvData = arrData.map((row) =>
            headerRow
                .map((fieldName) => {
                    let cellValue = row.get(fieldName);
                    if (typeof cellValue === 'string' && cellValue.includes('/')) {
                        cellValue = cellValue.replace(/\//g, '\n');
                        cellValue = cellValue.replace(/"/g, '""');
                        return `"${cellValue}"`;
                    }
                    return JSON.stringify(cellValue, replacer);
                })
                .join(','),
        );

        csvData.unshift(headerRow.join(','));

        const csvArray = csvData.join('\r\n');
        const blob = new Blob([csvArray], {type: 'text/csv'});
        FileSaver.saveAs(blob, `${fileName}.csv`);
    }

    // Prepare CSV data and send to browser
    static downloadChartData(customData: ChartDownloadData, fileName: string) {
        let headerRow: string[] = customData.header;
        const arrData: any[] = customData.data;
        let row = arrData[0];
        let dataKey = Object.keys(row || []);
        const replacer = (key, value) => (value === null ? '' : value);

        // Create header row if it was not directly provided
        if (headerRow === null) {
            headerRow = dataKey;
        } else if (dataKey.length === headerRow.length && headerRow.every((element) => element in row)) {
            dataKey = headerRow;
        }

        // Create CSV data for download
        const csvData = arrData.map((row) =>
            dataKey
                .map((fieldName) => {
                    return JSON.stringify(row[fieldName], replacer);
                })
                .join(','),
        );
        csvData.unshift(headerRow.join(','));
        // Create CSV file and send to browser
        const csvArray = csvData.join('\r\n');
        const blob = new Blob([csvArray], {type: 'text/csv'});
        FileSaver.saveAs(blob, `${fileName}.csv`);
    }

    static downloadChartImage(chartInstance: ECharts, fileName: string, format: 'png' | 'jpg') {
        let img = new Image();
        img.src = chartInstance.getDataURL({
            // @ts-ignore
            type: format,
        });

        FileSaver.saveAs(img.src, `${fileName}.${format}`);
    }
}
